import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

/** 
 * Service used to store and retrieve user message state, editing state, and editing message ID.
 * This allows us to restore the last user message, editing state, and which message is being edited after minimize.
 */
@Injectable({
  providedIn: 'root',
})
export class InputService {
  private _userMessageHTML: string = '';
  private _isEditingMessage: boolean = false;
  private _editingMessageId: string | null = null;
  private _selectedChatbotModeCode: string | null = null;

  private _currentMessage = new Subject<string>();
  public currentMessage$ = this._currentMessage.asObservable();

  private _appendToInput = new Subject<string>();
  public appendToInput$ = this._appendToInput.asObservable();

  private _pendingAppendQueue: string[] = [];

  constructor() { }

  /**
   * Queues copied text for later append and emits it immediately to active input subscribers.
   * Use this when copy actions can occur before the input component is rendered.
   * @param text HTML/text fragment to append to the current input content.
   */
  public queueAppendToInput(text: string): void {
    this._pendingAppendQueue.push(text);
    this._appendToInput.next(text);
  }

  /**
   * Returns all queued append requests and clears the internal queue.
   * Typically called once after the input component finishes rendering.
   * @returns A snapshot of pending append items in FIFO order.
   */
  public clearPendingAppends(): string[] {
    const items = [...this._pendingAppendQueue];
    this._pendingAppendQueue = [];
    return items;
  }

  public setCurrentMessage(messageHTML: string): void {
    this._userMessageHTML = messageHTML;
  }

  public getCurrentMessage(): string {
    return this._userMessageHTML;
  }

  public clearStoredMessage(): void {
    this._userMessageHTML = '';
    this._currentMessage.next('');
  }

  public setIsEditingMessage(isEditing: boolean): void {
    this._isEditingMessage = isEditing;
  }

  public getIsEditingMessage(): boolean {
    return this._isEditingMessage;
  }

  public setEditingMessageId(messageId: string | null): void {
    this._editingMessageId = messageId;
  }

  public getEditingMessageId(): string | null {
    return this._editingMessageId;
  }

  public setSelectedChatbotModeCode(modeCode: string | null): void {
    this._selectedChatbotModeCode = modeCode;
  }

  public getSelectedChatbotModeCode(): string | null {
    return this._selectedChatbotModeCode;
  }

  public clearSelectedChatbotModeCode(): void {
    this._selectedChatbotModeCode = null;
  }

  public pushExternalMessage(message: string): void {
    this._currentMessage.next(message);
  }
}
